import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import usePersistentState from "@/hooks/usePersistentState";

interface OverviewStats {
  totalUsers: number;
  todaySignups: number;
  monthlyCharged: number;
  monthlySent: number;
  successRate: number;
}

interface RecentUser {
  id: string;
  full_name: string | null;
  email: string | null;
  plan: string;
  created_at: string;
  status: string;
  credit: number;
}

interface RecentCharge {
  id: string;
  amount: number;
  description: string | null;
  payment_method: string | null;
  created_at: string;
  user_email: string;
}

interface PlanDist {
  plan: string;
  count: number;
}

interface DailySignup {
  day: string;
  count: number;
}

interface OverviewCache {
  stats: OverviewStats;
  recentUsers: RecentUser[];
  recentCharges: RecentCharge[];
  planDist: PlanDist[];
  dailySignups: DailySignup[];
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

// 탭 복귀(리로드) 시 로딩 없이 마지막 지표를 즉시 보여주기 위한 캐시
const OVERVIEW_CACHE_KEY = "cache:admin_overview";

const EMPTY_CACHE: OverviewCache = {
  stats: { totalUsers: 0, todaySignups: 0, monthlyCharged: 0, monthlySent: 0, successRate: 0 },
  recentUsers: [],
  recentCharges: [],
  planDist: [],
  dailySignups: [],
};

function readOverviewCache(): OverviewCache | null {
  try {
    const raw = sessionStorage.getItem(OVERVIEW_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OverviewCache;
    if (!parsed || typeof parsed !== "object" || !parsed.stats) return null;
    return {
      stats: parsed.stats,
      recentUsers: Array.isArray(parsed.recentUsers) ? parsed.recentUsers : [],
      recentCharges: Array.isArray(parsed.recentCharges) ? parsed.recentCharges : [],
      planDist: Array.isArray(parsed.planDist) ? parsed.planDist : [],
      dailySignups: Array.isArray(parsed.dailySignups) ? parsed.dailySignups : [],
    };
  } catch {
    return null;
  }
}

function writeOverviewCache(data: OverviewCache): void {
  try {
    sessionStorage.setItem(OVERVIEW_CACHE_KEY, JSON.stringify(data));
  } catch {
    // 캐시 저장 실패는 무시
  }
}

const PLAN_COLORS: Record<string, string> = {
  FREE: "bg-gray-500/20 text-gray-400",
  BASIC: "bg-sky-500/20 text-sky-400",
  PRO: "bg-violet-500/20 text-violet-400",
  ENTERPRISE: "bg-rose-500/20 text-rose-400",
};

const PLAN_BAR_COLORS: Record<string, string> = {
  FREE: "bg-gray-500",
  BASIC: "bg-sky-500",
  PRO: "bg-violet-500",
  ENTERPRISE: "bg-rose-500",
};

const PAYMENT_LABELS: Record<string, string> = {
  card: "카드",
  kakao: "카카오페이",
  naver: "네이버페이",
  bank: "무통장",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function AdminOverview() {
  const cachedRef = useRef<OverviewCache | null>(readOverviewCache());
  const [period, setPeriod] = usePersistentState<"today" | "week" | "month">("admin_overview_period", "month");
  const [loading, setLoading] = useState(cachedRef.current === null);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<OverviewStats>(cachedRef.current?.stats ?? EMPTY_CACHE.stats);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>(cachedRef.current?.recentUsers ?? []);
  const [recentCharges, setRecentCharges] = useState<RecentCharge[]>(cachedRef.current?.recentCharges ?? []);
  const [planDist, setPlanDist] = useState<PlanDist[]>(cachedRef.current?.planDist ?? []);
  const [dailySignups, setDailySignups] = useState<DailySignup[]>(cachedRef.current?.dailySignups ?? []);
  const { session } = useAuth();

  const fetchData = useCallback(async () => {
    // 캐시된 지표가 이미 있으면 로딩 화면 없이 조용히 갱신
    if (cachedRef.current === null) setLoading(true);
    setError(null);
    try {
      const accessToken = session?.access_token;
      if (!accessToken) {
        return;
      }

      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6).toISOString();
      const periodStart = period === "today" ? todayStart : period === "week" ? weekStart : monthStart;

      // 1. 회원 목록 — get-admin-users Edge Function
      const usersRes = await fetch(`${SUPABASE_URL}/functions/v1/get-admin-users`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
      });

      let allProfiles: RecentUser[] = [];
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        allProfiles = (usersData.users ?? []).map((u: any) => ({
          id: u.id,
          full_name: u.full_name || u.name || null,
          email: u.email || null,
          plan: u.plan || "FREE",
          created_at: u.created_at,
          status: u.status || "active",
          credit: u.credit || 0,
        }));
      } else {
        const errData = await usersRes.json().catch(() => null);
        console.warn("[AdminOverview] get-admin-users failed:", errData);
        setError("관리자 권한으로 회원 데이터를 불러오지 못했습니다.");
        setLoading(false);
        return;
      }

      // 2. 충전 내역 — get-admin-credit-transactions Edge Function
      const chargeRes = await fetch(
        `${SUPABASE_URL}/functions/v1/get-admin-credit-transactions?user_id=`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            apikey: SUPABASE_ANON_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      let chargeData: any[] = [];
      if (chargeRes.ok) {
        const chargeJson = await chargeRes.json();
        chargeData = chargeJson.transactions ?? [];
      } else {
        console.warn("[AdminOverview] get-admin-credit-transactions failed");
      }

      const monthChargeTotal = chargeData
        .filter((t: any) => t.type === "charge" && t.created_at >= monthStart)
        .reduce((s: number, t: any) => s + (t.amount ?? 0), 0);

      const todayCharges = chargeData
        .filter((t: any) => t.type === "charge" && t.created_at >= todayStart)
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 5)
        .map((c: any) => {
          const profile = allProfiles.find((p) => p.id === c.user_id);
          return {
            id: c.id,
            amount: c.amount,
            description: c.description,
            payment_method: c.payment_method,
            created_at: c.created_at,
            user_email: profile?.email ?? profile?.full_name ?? "알 수 없음",
          };
        });

      // 3. 발송 통계 — get-admin-send-history Edge Function
      const sendRes = await fetch(
        `${SUPABASE_URL}/functions/v1/get-admin-send-history?user_id=`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            apikey: SUPABASE_ANON_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      let sendData: any[] = [];
      if (sendRes.ok) {
        const sendJson = await sendRes.json();
        sendData = sendJson.send_history ?? [];
      } else {
        console.warn("[AdminOverview] get-admin-send-history failed");
      }

      const sendItems = sendData.filter((h: any) => h.status !== "scheduled" && h.sent_at >= periodStart);
      const totalSuccess = sendItems.reduce((s: number, h: any) => s + (h.success_count ?? 0), 0);
      const totalFail = sendItems.reduce((s: number, h: any) => s + (h.fail_count ?? 0), 0);
      const totalSent = totalSuccess + totalFail;
      const rate = totalSent > 0 ? Math.round((totalSuccess / totalSent) * 100) : 0;

      // 오늘 가입자 수
      const todayCount = allProfiles.filter((p) => p.created_at >= todayStart).length;

      const nextStats: OverviewStats = {
        totalUsers: allProfiles.length,
        todaySignups: todayCount,
        monthlyCharged: monthChargeTotal,
        monthlySent: totalSent,
        successRate: rate,
      };

      // 최근 가입 5명
      const nextRecentUsers = allProfiles.slice(0, 5);

      // 플랜 분포
      const planCount: Record<string, number> = { FREE: 0, BASIC: 0, PRO: 0, ENTERPRISE: 0 };
      allProfiles.forEach((p) => {
        const plan = p.plan || "FREE";
        planCount[plan] = (planCount[plan] ?? 0) + 1;
      });
      const nextPlanDist: PlanDist[] = Object.entries(planCount)
        .map(([plan, count]) => ({ plan, count }))
        .sort((a, b) => b.count - a.count);

      // 최근 7일 일별 가입자
      const days: DailySignup[] = [];
      const weekProfiles = allProfiles.filter((p) => p.created_at >= weekStart);
      for (let i = 6; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
        const dayEnd = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).toISOString();
        const count = weekProfiles.filter((u) => u.created_at >= dayStart && u.created_at < dayEnd).length;
        const weekDays = ["일", "월", "화", "수", "목", "금", "토"];
        days.push({ day: weekDays[d.getDay()], count });
      }

      setStats(nextStats);
      setRecentUsers(nextRecentUsers);
      setRecentCharges(todayCharges);
      setPlanDist(nextPlanDist);
      setDailySignups(days);

      writeOverviewCache({
        stats: nextStats,
        recentUsers: nextRecentUsers,
        recentCharges: todayCharges,
        planDist: nextPlanDist,
        dailySignups: days,
      });
    } catch (err: any) {
      console.error("[AdminOverview] fetch error:", err);
      setError(err.message || "데이터를 불러오는 중 오류가 발생했습니다.");
    } finally {
      cachedRef.current = cachedRef.current ?? EMPTY_CACHE;
      setLoading(false);
    }
  }, [period, session]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const maxSignup = Math.max(...dailySignups.map((d) => d.count), 1);
  const totalUsers = stats.totalUsers || 1;

  const statsCards = [
    {
      label: "전체 회원", value: loading ? "..." : stats.totalUsers.toLocaleString(),
      change: `+${stats.todaySignups}`, changeLabel: "오늘 가입",
      icon: "ri-user-3-line", color: "from-rose-500/20 to-orange-500/10", iconColor: "text-rose-400",
    },
    {
      label: "오늘 가입", value: loading ? "..." : stats.todaySignups.toLocaleString(),
      change: stats.todaySignups > 0 ? "신규" : "없음", changeLabel: "오늘 기준",
      icon: "ri-user-add-line", color: "from-amber-500/20 to-yellow-500/10", iconColor: "text-amber-400",
    },
    {
      label: period === "today" ? "오늘 충전" : period === "week" ? "이번 주 충전" : "이번 달 충전",
      value: loading ? "..." : stats.monthlyCharged >= 1000000
        ? `₩${(stats.monthlyCharged / 1000000).toFixed(1)}M`
        : `₩${stats.monthlyCharged.toLocaleString()}`,
      change: stats.monthlyCharged > 0 ? "충전됨" : "없음", changeLabel: "기간 합계",
      icon: "ri-coin-line", color: "from-emerald-500/20 to-teal-500/10", iconColor: "text-emerald-400",
    },
    {
      label: "기간 발송",
      value: loading ? "..." : stats.monthlySent >= 10000
        ? `${(stats.monthlySent / 10000).toFixed(1)}만`
        : stats.monthlySent.toLocaleString(),
      change: stats.monthlySent > 0 ? "건" : "없음", changeLabel: "건",
      icon: "ri-send-plane-line", color: "from-sky-500/20 to-cyan-500/10", iconColor: "text-sky-400",
    },
    {
      label: "발송 성공률",
      value: loading ? "..." : `${stats.successRate}%`,
      change: stats.successRate >= 95 ? "우수" : stats.successRate >= 80 ? "보통" : "개선필요",
      changeLabel: "기간 기준",
      icon: "ri-checkbox-circle-line", color: "from-violet-500/20 to-purple-500/10", iconColor: "text-violet-400",
    },
    {
      label: "전체 발송 합계",
      value: loading ? "..." : `${stats.monthlySent.toLocaleString()}건`,
      change: "누적", changeLabel: "기간 내",
      icon: "ri-bar-chart-2-line", color: "from-pink-500/20 to-rose-500/10", iconColor: "text-pink-400",
    },
  ];

  if (error && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-red-500/10 border border-red-500/20 mb-4">
          <i className="ri-error-warning-line text-3xl text-red-400"></i>
        </div>
        <h3 className="text-white font-semibold text-lg mb-2">데이터 로드 실패</h3>
        <p className="text-gray-500 text-sm max-w-md">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-5 py-2.5 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] text-gray-300 text-sm font-medium cursor-pointer transition-colors"
        >
          <i className="ri-refresh-line mr-1.5"></i>
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-semibold text-lg">대시보드 개요</h2>
          <p className="text-gray-500 text-sm mt-0.5">서비스 전반의 핵심 지표를 한눈에</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white cursor-pointer transition-colors"
            title="새로고침"
          >
            <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`}></i>
          </button>
          <div className="flex items-center gap-1 bg-white/[0.05] rounded-lg p-1">
            {(["today", "week", "month"] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                  period === p ? "bg-rose-600 text-white" : "text-gray-400 hover:text-white"
                }`}
              >
                {p === "today" ? "오늘" : p === "week" ? "이번 주" : "이번 달"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {statsCards.map((stat) => (
          <div key={stat.label} className={`bg-gradient-to-br ${stat.color} border border-white/[0.08] rounded-2xl p-5`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/[0.08]">
                <i className={`${stat.icon} text-lg ${stat.iconColor}`}></i>
              </div>
              <span className="text-emerald-400 text-xs font-semibold bg-emerald-400/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                {stat.change}
              </span>
            </div>
            {loading ? (
              <div className="h-8 w-24 bg-white/[0.06] rounded-lg animate-pulse mb-1"></div>
            ) : (
              <p className="text-white font-bold text-2xl">{stat.value}</p>
            )}
            <p className="text-gray-400 text-xs mt-1">{stat.label}</p>
            <p className="text-gray-600 text-xs">{stat.changeLabel}</p>
          </div>
        ))}
      </div>

      {/* Middle row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Weekly signup chart */}
        <div className="lg:col-span-2 bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-white font-semibold text-sm">최근 7일 신규 가입자</h3>
              <p className="text-gray-500 text-xs mt-0.5">일별 가입 추이</p>
            </div>
            <span className="text-rose-400 text-sm font-bold">
              총 {dailySignups.reduce((s, d) => s + d.count, 0)}명
            </span>
          </div>
          {loading ? (
            <div className="h-32 flex items-end gap-3">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="flex-1 bg-white/[0.06] rounded-t-md animate-pulse" style={{ height: `${Math.random() * 80 + 20}px` }}></div>
              ))}
            </div>
          ) : (
            <div className="flex items-end gap-3 h-32">
              {dailySignups.map((d, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-gray-400 text-xs">{d.count}</span>
                  <div
                    className="w-full rounded-t-md bg-gradient-to-t from-rose-600 to-orange-500 transition-all min-h-[4px]"
                    style={{ height: `${Math.max(4, (d.count / maxSignup) * 96)}px` }}
                  ></div>
                  <span className="text-gray-500 text-xs">{d.day}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Plan distribution */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6">
          <h3 className="text-white font-semibold text-sm mb-1">플랜 분포</h3>
          <p className="text-gray-500 text-xs mb-5">전체 {stats.totalUsers.toLocaleString()}명</p>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-1.5">
                  <div className="h-3 w-24 bg-white/[0.06] rounded animate-pulse"></div>
                  <div className="h-1.5 bg-white/[0.06] rounded-full animate-pulse"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {planDist.map((p) => {
                const pct = Math.round((p.count / totalUsers) * 100);
                return (
                  <div key={p.plan}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-gray-300 text-xs font-medium">{p.plan}</span>
                      <span className="text-gray-400 text-xs">{p.count.toLocaleString()}명 ({pct}%)</span>
                    </div>
                    <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                      <div
                        className={`h-full ${PLAN_BAR_COLORS[p.plan] ?? "bg-gray-500"} rounded-full transition-all`}
                        style={{ width: `${pct}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-white font-semibold text-sm">최근 가입 회원</h3>
            <span className="text-rose-400 text-xs">최근 5명</span>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/[0.06] animate-pulse shrink-0"></div>
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-24 bg-white/[0.06] rounded animate-pulse"></div>
                    <div className="h-2 w-36 bg-white/[0.04] rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentUsers.length === 0 ? (
            <div className="py-8 text-center">
              <i className="ri-user-line text-gray-600 text-2xl block mb-2"></i>
              <p className="text-gray-500 text-sm">가입된 회원이 없습니다</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentUsers.map((u) => {
                const displayName = u.full_name || u.email || "이름 없음";
                return (
                  <div key={u.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/[0.04] transition-colors">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-orange-500 text-white text-xs font-semibold shrink-0">
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{displayName}</p>
                      <p className="text-gray-500 text-xs truncate">{u.email}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${PLAN_COLORS[u.plan] ?? PLAN_COLORS.FREE}`}>
                        {u.plan || "FREE"}
                      </span>
                      <p className="text-gray-600 text-xs mt-0.5">{formatDate(u.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Charges */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-white font-semibold text-sm">오늘 충전 내역</h3>
            <span className="text-rose-400 text-xs">오늘 기준</span>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/[0.06] animate-pulse shrink-0"></div>
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-28 bg-white/[0.06] rounded animate-pulse"></div>
                    <div className="h-2 w-20 bg-white/[0.04] rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentCharges.length === 0 ? (
            <div className="py-8 text-center">
              <i className="ri-wallet-3-line text-gray-600 text-2xl block mb-2"></i>
              <p className="text-gray-500 text-sm">오늘 충전 내역이 없습니다</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentCharges.map((c) => (
                <div key={c.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/[0.04] transition-colors">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 shrink-0">
                    <i className="ri-checkbox-circle-line text-sm"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{c.user_email}</p>
                    <p className="text-gray-500 text-xs whitespace-nowrap">
                      {c.payment_method ? (PAYMENT_LABELS[c.payment_method] ?? c.payment_method) : "-"} · {formatTime(c.created_at)}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-emerald-400 text-sm font-semibold whitespace-nowrap">+₩{c.amount.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}